import React, { useState, useEffect, useMemo } from 'react'
import { Link } from 'react-router-dom'
import { GoBackLink } from '../GoBackLink'
import { Table } from 'react-bootstrap'
import { Switch } from '@CommonComponents/buttons'
import { useStateValue } from '@State'
import { client } from '@Utils'
import { RemoveConfirmModal } from '../modals/RemoveConfirmModal'
import { toast } from '@CommonComponents/toast'
import { EditIssuerModal } from '../modals/EditIssuerModal'

// Presentational grouping for the Canvas issuer only. The ids are Darin client ids -- the
// i58-encoded uid dserv serializes as `id` (see voshi.uid.UidModel.iid), which is what the
// ID column of this table shows and what the ticket listed. They are NOT the `clientid`
// field, which holds the numeric client_id Canvas itself issues.
//
// This mapping lives in the frontend because there is no backend concept of a section; a
// client added in Darin without being listed here still renders, under UNLISTED_SECTION_TITLE.
const CANVAS_CLIENT_SECTIONS = [
  {
    title: 'Production Section (Inherited Key Install)',
    clientIds: ['19PVZ7sLnbKp'],
  },
  {
    title: 'Production Section (Institution-Specific Key Install)',
    clientIds: ['19L6ras9ZBcn', '19LZtCg2GLdg', '19MxgE6tbVte', 'I9Nkvw9yr3er', 'I9ZCyMvCvSsL'],
  },
  {
    title: 'Testing Section (Internal, External, and Sandbox)',
    clientIds: ['19N6vPiqBeCa', 'I9MxeewErzv6', 'I9YCiFAFxwZL'],
  },
]

const UNLISTED_SECTION_TITLE = 'Unlisted Clients (not yet assigned to a section)'

const CANVAS_SECTIONED_CLIENT_IDS = new Set(
  CANVAS_CLIENT_SECTIONS.flatMap(section => section.clientIds)
)

const ClientsTable = ({
  clients,
  isPrivileged,
  lmsCode,
  issuerId,
  openModal,
}) => (
  <div className="table-responsive">
    <Table striped bordered className="table-sm" hover>
      <thead>
        <tr>
          <th>ID</th>
          <th>Name</th>
          <th>Enabled</th>
          <th>Client ID</th>
          <th>Actions</th>
          {isPrivileged && <th>Remove</th>}
        </tr>
      </thead>
      <tbody>
        {clients.length === 0 ? (
          <tr>
            <td colSpan={isPrivileged ? 6 : 5} className="text-muted">
              No clients in this section.
            </td>
          </tr>
        ) : (
          clients.map(clientObj => (
            <tr key={clientObj.id}>
              <td>{clientObj.id}</td>
              <td>{clientObj.data.name}</td>
              <td>{clientObj.data.lti_login ? 'Yes' : 'No'}</td>
              <td>{clientObj.clientid}</td>
              <td>
                <Link
                  to={`/account/admin_lti13/-/platforms/${lmsCode}/issuers/${issuerId}/clients/${clientObj.id}`}
                >
                  View Client
                </Link>
              </td>
              {isPrivileged && (
                <td>
                  <a
                    onClick={() => openModal(clientObj)}
                    className="btn btn-sm btn-sm-myed btn-danger"
                  >
                    Remove <i className="fa-regular fa-window-close" />
                  </a>
                </td>
              )}
            </tr>
          ))
        )}
      </tbody>
    </Table>
  </div>
)

export const Issuer = ({ match }) => {
  const [currentState, dispatch] = useStateValue()
  const { text } = window.MEButtons

  const [showIssuerEditModal, setShowIssuerEditModal] = useState(false)

  const [removeContextModalOpen, toggleRemoveModal] = useState(false)
  const [removeContextURL, setRemoveContextURL] = useState('')
  const [removeContextMessage, setRemoveContextMessage] = useState('')

  useEffect(() => {
    const getClients = async () => {
      try {
        let resp = await client.get(
          `/account/admin_lti13.getClients/-/${match.params.issuerId}/`
        )
        dispatch({ type: 'updateMessage', message: null })
        dispatch({ type: 'updateIssuer', issuer: resp.data.issuer })
        dispatch({
          type: 'updateClientsTable',
          clientsTableData: resp.data.clients,
        })
      } catch (err) {
        if (err.response) {
          dispatch({ type: 'updateMessage', message: err.response.statusText })
          toast.error(`${err.response.statusText} ${err.response.data.error}`)
        } else {
          dispatch({ type: 'updateMessage', message: err })
        }
      }
    }
    getClients()
  }, [currentState.reloadSwitch])

  const openModal = contextObject => {
    setRemoveContextURL(
      `/account/admin_lti13.deleteClient/-/${contextObject.id}/`
    )
    setRemoveContextMessage(
      `Are you sure you want to remove Client: ${contextObject.data.name}?`
    )
    toggleRemoveModal(true)
  }

  // Group the Canvas clients into their named sections, then sweep anything Darin returned
  // that no section claims into a trailing catch-all. Without that last bucket a client added
  // in Darin but not listed above would vanish from this page with no indication it exists.
  const canvasClientSections = useMemo(() => {
    const clients = currentState.clientsTableData || []
    const sections = CANVAS_CLIENT_SECTIONS.map(section => ({
      title: section.title,
      clients: clients.filter(c => section.clientIds.includes(c.id)),
    }))
    const unlisted = clients.filter(c => !CANVAS_SECTIONED_CLIENT_IDS.has(c.id))
    if (unlisted.length > 0) {
      sections.push({ title: UNLISTED_SECTION_TITLE, clients: unlisted })
    }
    return sections
  }, [currentState.clientsTableData])

  return (
    <>
      {currentState.isPrivileged && (
        <div className="mb-2 go-back-button-left-align">
          <GoBackLink to={`/account/admin_lti13/-/platforms/${match.params.lmsCode}/issuers`} />
        </div>
      )}
      <div className="card-myed">
        {currentState.isPrivileged && (
          <div className="pi-info pull-right">
            <button
              onClick={() => setShowIssuerEditModal(true)}
              className="btn btn-sm btn-sm-myed btn-primary"
            >
              Edit <i className="fa-regular fa-edit" />
            </button>
          </div>
        )}
        <h3>
          Issuer: {currentState.platform && currentState.platform.data.name}
        </h3>

        {currentState.issuer && (
          <div>
            {currentState.isPrivileged && (
              <div className="pull-right">
                <Switch
                  label="Enable LTI 1.3 Connection"
                  data-busy-label={text('strSaving')}
                  data-success-label={text('strChangesSaved')}
                  data-failure-label={text('strSaveFailed')}
                  data-failure-text={text('strTryAgain')}
                  onChange={async () => {
                    let data = {
                      lti_login: !currentState.issuer.data.lti_login,
                    }
                    try {
                      let resp = await client.post(
                        `/account/admin_lti13.lti_login/-/${match.params.issuerId}`,
                        data
                      )
                      dispatch({
                        type: 'updateIssuer',
                        issuer: resp.data.issuer,
                      })
                      if(resp.data.issuer.data.lti_login) {
                        toast.success('LTI Login Status Updated! LTI login is now: Enabled')
                      } else {
                        toast.warn('LTI Login Status Updated! LTI login is now: Disabled')
                      }
                    } catch (err) {
                      toast.error(`There was an error trying to toggle the Login status: ${err}`)
                      throw err
                    }
                  }}
                  checked={!!currentState.issuer.data.lti_login}
                  disabled={
                    !['moodle', 'desire2learn'].includes(
                      currentState.issuer.data['product_family_code']
                    )
                  }
                />
                {['moodle', 'desire2learn'].includes(
                  currentState.issuer.data['product_family_code']
                ) && (
                  <p>Issuers should be enabled for Brightspace and Moodle</p>
                )}
              </div>
            )}
            <p>
              <span style={{ fontSize: '1.2em', fontWeight: 'bolder' }}>
                ID:
              </span>{' '}
              {currentState.issuer.id}
            </p>
            <p>
              <span style={{ fontSize: '1.2em', fontWeight: 'bolder' }}>
                Name:
              </span>{' '}
              {currentState.issuer.data.name}
            </p>
            <p>
              <span style={{ fontSize: '1.2em', fontWeight: 'bolder' }}>
                Connection Enabled:{' '}
              </span>{' '}
              {currentState.issuer.data.lti_login ? 'Yes' : 'No'}
            </p>
            <p>
              <span style={{ fontSize: '1.2em', fontWeight: 'bolder' }}>
                Issuer:
              </span>{' '}
              {currentState.issuer.iss}
            </p>
          </div>
        )}
      </div>

      {match.params.lmsCode === 'canvas'
        ? canvasClientSections.map(section => (
            <div className="card-myed" key={section.title}>
              <h3>{section.title}:</h3>
              <ClientsTable
                clients={section.clients}
                isPrivileged={currentState.isPrivileged}
                lmsCode={match.params.lmsCode}
                issuerId={match.params.issuerId}
                openModal={openModal}
              />
            </div>
          ))
        : (
          <div className="card-myed">
            <h3>Clients:</h3>
            <ClientsTable
              clients={currentState.clientsTableData || []}
              isPrivileged={currentState.isPrivileged}
              lmsCode={match.params.lmsCode}
              issuerId={match.params.issuerId}
              openModal={openModal}
            />
          </div>
        )}
      <EditIssuerModal
        show={showIssuerEditModal}
        toggle={setShowIssuerEditModal}
        issuer={currentState.issuer}
      />
      <RemoveConfirmModal
        modalTitle="Remove Client"
        message={removeContextMessage}
        endpoint={removeContextURL}
        show={removeContextModalOpen}
        toggle={toggleRemoveModal}
        currentState={currentState}
        dispatch={dispatch}
      />
    </>
  )
}
